/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Renderer2,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { XdaStringsService } from '../../services/xda-strings.service';
import { BrowserService } from '../../services/browser.service';
import { OrderBy } from '../../pipes/order-by.pipe';
import { SettingType } from '../../../common/enums';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { XrxMultiSelectComponent } from '../controls/xrx-multi-select.component';
import { XrxControlBaseComponent } from './xrx-control-base.component';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { OptionListOptions, Parameter } from '../../services/workflow.service';

@Component({
  selector: 'xrx-popover',
  templateUrl: './xrx-popover.component.html',
  styleUrls: ['./xrx-popover.component.less'],
  providers: [
    { provide: XrxControlBaseComponent, useExisting: XrxPopoverComponent },
  ],
  standalone: false,
})
export class XrxPopoverComponent
  extends XrxControlBaseComponent
  implements OnInit, OnDestroy
{
  //WIP
  showPopover = false;
  blanket: Renderer2;
  parent: HTMLElement | null;
  wrapper: HTMLElement | null;
  orderByPipe: OrderBy<OptionListOptions>;
  browserType: number;
  selectedItems: string | null = null;

  @ViewChild('popover', { static: false }) popover: ElementRef;
  @ViewChild('arrow', { static: false }) arrow: ElementRef;

  @Input() param: Parameter;
  @Input() list: any[];
  @Input() name: string;
  @Input() showLabel: boolean;
  @Input() selectionType: SettingType;
  @Input() showLabelOnTop = false;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() selectedValue: any;
  @Input() showIcon = true;
  @Input() valuePosition = 'right';
  @Input() dir = 'asc';
  @Input() orderByValue = 'key';
  @Input() sortOptions: boolean;
  @Input() padTopLabel: boolean;
  @Input() disableIScroll = false;
  @Input() key = 'key';
  @Input() property = 'value';
  @Input() required: boolean;
  @Input() error: string;
  @Input() inlineLabel: string;

  @Output() selectedValueChange = new EventEmitter<any>();
  @Output() change = new EventEmitter<any>();
  @Output() validation = new EventEmitter<any>();
  @Output() paramChange = new EventEmitter<any>();

  private _value: string;
  @Input() set value(updatedValue: string) {
    if (updatedValue) {
      this._value = updatedValue;
      this.selectedValue = this.list.find(
        (option) => option[this.property] === this.value,
      );
    }
  }
  get value(): string {
    return this._value;
  }
  private xdaTranslatePipe: XdaTranslate;

  constructor(
    private renderer: Renderer2,
    private _xdaStringsService: XdaStringsService,
    private browserService: BrowserService,
    private modalService: NgbModal,
  ) {
    super();
    this.orderByPipe = new OrderBy(this._xdaStringsService);
    this.browserType = browserService.browserType();
    this.xdaTranslatePipe = new XdaTranslate(this._xdaStringsService);
  }

  ngOnInit() {
    this.parent = document.querySelector('.modal-open');
    this.name = this.name || this.param.name;
    this.label = this.label || this.param.label;

    if (this.placeholder) {
      this.label = `${this.xdaTranslatePipe.transform(this.label)} (${this.xdaTranslatePipe.transform(this.placeholder, ['5'])})`;
    }

    if (!this.list) {
      this.list = this.param.options;
    }

    if (!this.selectionType) {
      this.selectionType = this.param?.type;
    }

    if (this.value) {
      this.selectedValue = this.list.find(
        (option) => option[this.property] === this.value,
      );
    } else {
      this.value = this.param?.value || '';
      if (!this.selectedValue) {
        this.selectedValue = this.param?.selectedValue ?? '';
      }
    }

    this.generateSelectedItemList();

    if (this.sortOptions) {
      this.orderByPipe.transform(this.list, this.dir, this.orderByValue);
    }
  }

  public get SettingType(): typeof SettingType {
    return SettingType;
  }

  displayPopover(event: MouseEvent | Event) {
    this.addBlanket();
    this.showPopover = true;
    setTimeout(() => {
      this.appendPopover();
      this.positionPopover(event as MouseEvent);
    });
  }

  displayMultiSelect() {
    const modalRef = this.modalService.open(XrxMultiSelectComponent, {
      windowClass: 'xrx-popup',
    });
    modalRef.componentInstance.title = this.param.label;
    modalRef.componentInstance.localize = 'key';
    modalRef.componentInstance.setting = this.param;
    modalRef.componentInstance.list = this.list;
    modalRef.componentInstance.required = true;
    modalRef.componentInstance.leftAlignText = true;
    modalRef.componentInstance.isBordered = true;
    modalRef.componentInstance.hasScrollbar = true;
    modalRef.componentInstance.maximumItemsSelected = 5;
    modalRef.componentInstance.isMfd = true;
    // When a user selects a to language, verify if errors should be cleared
    modalRef.componentInstance.validation.subscribe(
      (param: Parameter) => {
        this.validation.emit(param);
      },
      () => {},
    );
    // When a user selects a to language, store it
    modalRef.componentInstance.change.subscribe(
      () => {
        this.generateSelectedItemList();
        this.change.emit(this.selectedItems);
      },
      () => {},
    );
  }

  addBlanket() {
    this.blanket = this.renderer.createElement('div');
    this.renderer.addClass(this.blanket, 'blanket');
    this.renderer.appendChild(this.parent, this.blanket);
    this.renderer.listen(this.blanket, 'click', () => {
      this.closePopover();
    });
  }

  positionPopover(e: MouseEvent) {
    const winHeight = window.innerHeight;
    const winWidth = window.innerWidth;
    const contents = document.querySelector('.popover-body');
    const arrow = document.querySelector('.arrow');

    const height = contents?.clientHeight || 1;
    const width = contents?.clientWidth || 1;

    let top = 0;
    const mid = height / 2;
    top = Math.max(2, e.pageY - mid);

    let float = 'left';
    let arrowLeft = 0;
    let transform = 'none';
    top = Math.max(8, top);

    const arrowWidth = arrow?.clientWidth || 1;
    const arrowHeight = arrow?.clientHeight || 1;

    const totalSize = width + arrowWidth;
    const totalHeight = height + arrowHeight;
    const availableSpaceOnRight = winWidth - e.pageX;
    const availableSpaceOnLeft = winWidth - availableSpaceOnRight;
    const availableSpaceOnBottom = winHeight - e.pageY;
    const availableSpaceOnTop = winHeight - availableSpaceOnBottom;
    let calcLeft = e.pageX - totalSize;

    if (totalSize < availableSpaceOnRight) {
      calcLeft = e.pageX + arrowWidth;
      arrowLeft = e.pageX;
      transform = `translate(${arrowLeft}px)`;
      float = 'right';
    } else if (totalSize < availableSpaceOnLeft) {
      arrowLeft = e.pageX - arrowWidth;
      transform = `translate(${arrowLeft}px)`;
    } else {
      calcLeft = (winWidth - totalSize) / 2;
      arrowLeft = e.pageX - arrowWidth / 2;
      transform = `translate(${arrowLeft}px) rotate(270deg)`;
    }

    if (top + (contents?.clientHeight || 1) >= winHeight) {
      const diff = winHeight - (top + (contents?.clientHeight || 1));
      top = top - Math.abs(diff) - 24;
    }

    if (top < 0) {
      this.popover.nativeElement.style.webkitTransform = `translate(${calcLeft}px)`;
      this.popover.nativeElement.style.bottom = '8px';
    } else {
      //TODO check top or bottom available space
      this.popover.nativeElement.style.webkitTransform = `translate(${calcLeft}px)`;
      this.popover.nativeElement.style.top = `${top}px`;
    }
    this.arrow.nativeElement.style.webkitTransform = transform;
    this.arrow.nativeElement.style.top = `${e.pageY - 25 / 2}px`;
    for (let i = 0; i < this.arrow.nativeElement.children.length; i++) {
      this.arrow.nativeElement.children[i].style.float = float;
    }
  }

  appendPopover() {
    this.renderer.appendChild(this.parent, this.popover.nativeElement);
    this.renderer.appendChild(this.parent, this.arrow.nativeElement);
  }

  setValue(option: Record<string, string>) {
    this.selectedValue = option;
    this.value = option[this.property];
    this.selectedValueChange.emit(option);
    this.change.emit(option);
    this.closePopover();
  }

  closePopover() {
    this.hideBlanket();
    this.showPopover = false;
  }

  hideBlanket() {
    this.renderer.removeChild(this.parent, this.blanket);
  }

  generateSelectedItemList() {
    this.selectedItems = null;
    this.list.forEach((item) => {
      if (item.checked && !this.selectedItems) {
        this.selectedItems =
          this._xdaStringsService.strings[item.key] || item.key;
      } else if (item.checked) {
        this.selectedItems =
          this.selectedItems +
          ', ' +
          (this._xdaStringsService.strings[item.key] || item.key);
      }
    });
  }

  isErrorState() {
    return !this.selectedValue && (this.required || this.error);
  }

  validate() {
    this.validation.emit(this.required && !this.selectedValue ? false : true);
  }

  ngOnDestroy() {}
}
